import { useState, JSX } from 'react';
import { Stack, Group, Text, Badge, Button, Paper } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { Patient, Encounter } from '@medplum/fhirtypes';
import { ProcedureListItem, ProcedureAction } from './ProcedureListItem';
import { ProcedureInputModal } from './ProcedureInputModal';
import { useProcedureActions } from '@/hooks/useProcedureActions';
import {
  createNewSurgeryInputItem,
  SurgeryInputItem,
} from '@/utils/surgery.utils';

export interface ProceduresListProps {
  procedures: SurgeryInputItem[];
  onChange: (procedures: SurgeryInputItem[]) => void;
  patient: Patient;
  encounter: Encounter | undefined;
  isMedicalHistory?: boolean;
  readonly?: boolean;
}

export const ProceduresList = ({
  procedures,
  onChange,
  patient,
  encounter,
  isMedicalHistory = false,
  readonly = false,
}: ProceduresListProps): JSX.Element => {
  const { t } = useTranslation(['patient-pages']);

  // Use the procedure actions hook for server operations
  const { saveProcedure, deleteProcedure } = useProcedureActions(
    patient,
    encounter
  );

  // Modal states
  const [procedureModalOpened, setProcedureModalOpened] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState<
    SurgeryInputItem | undefined
  >();
  const [editingIndex, setEditingIndex] = useState<number | undefined>();
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [deletingIndex, setDeletingIndex] = useState<number | undefined>();

  // Handlers
  const handleAddProcedure = () => {
    if (!encounter) {
      notifications.show({
        color: 'red',
        title: t('surgeries.encounterRequired', {
          defaultValue: 'Encounter is required to add a procedure',
        }),
        message: t('surgeries.encounterRequiredMessage', {
          defaultValue: 'Please create an encounter first',
        }),
      });
      return;
    }

    const newProcedure = createNewSurgeryInputItem();
    setEditingProcedure(newProcedure);
    setEditingIndex(undefined);
    setModalMode('add');
    setProcedureModalOpened(true);
  };

  const handleAction = async (index: number, action: ProcedureAction) => {
    const procedure = procedures[index];

    switch (action) {
      case 'edit':
        setEditingProcedure(procedure);
        setEditingIndex(index);
        setModalMode('edit');
        setProcedureModalOpened(true);
        break;

      case 'remove': {
        // If the procedure has an ID, it's saved on server - delete it
        if (procedure.id) {
          setDeletingIndex(index);
          try {
            await deleteProcedure(procedure.id);
            // Remove from local state after successful server deletion
            const updated = procedures.filter((_, i) => i !== index);
            onChange(updated);
          } catch (error) {
            notifications.show({
              color: 'red',
              title: t('surgeries.deleteErrorTitle', {
                defaultValue: 'Failed to remove procedure',
              }),
              message: String(error),
            });
          } finally {
            setDeletingIndex(undefined);
          }
        } else {
          // Not saved yet - just remove from local state
          const updated = procedures.filter((_, i) => i !== index);
          onChange(updated);
        }
        break;
      }
    }
  };

  const handleSaveProcedure = (
    procedure: SurgeryInputItem,
    isMedicalHistoryParam?: boolean
  ): Promise<SurgeryInputItem> => {
    return saveProcedure(procedure, isMedicalHistoryParam ?? isMedicalHistory);
  };

  const handleSaveSuccess = (savedProcedure: SurgeryInputItem) => {
    if (editingIndex !== undefined) {
      // Update existing
      const updated = procedures.map((p, i) =>
        i === editingIndex ? savedProcedure : p
      );
      onChange(updated);
    } else {
      // Add new
      onChange([...procedures, savedProcedure]);
    }
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Group gap="xs">
        <Text fw={600} size="md">
          {t('surgeries.title', {
            ns: 'patient-pages',
            defaultValue: 'Surgeries',
          })}
        </Text>
        {procedures.length > 0 && (
          <Badge size="sm" variant="light">
            {procedures.length}
          </Badge>
        )}
      </Group>

      {/* Empty state */}
      {procedures.length === 0 ? (
        <Paper withBorder radius="md" p="xl" bg="gray.0">
          <Text size="sm" c="dimmed" ta="center">
            {t('surgeries.none', {
              ns: 'patient-pages',
              defaultValue: 'No procedures recorded.',
            })}
          </Text>
        </Paper>
      ) : (
        <>
          {/* Procedures List */}
          <Stack gap="xs">
            {procedures.map((procedure) => {
              // Find the actual index in the full procedures array
              const actualIndex = procedures.findIndex(
                (p) => p.id === procedure.id || p === procedure
              );
              return (
                <ProcedureListItem
                  key={procedure.id || `procedure-${actualIndex}`}
                  procedure={procedure}
                  onAction={(action) => handleAction(actualIndex, action)}
                  isDeleting={deletingIndex === actualIndex}
                  hideActions={readonly}
                />
              );
            })}
          </Stack>
        </>
      )}

      {/* Add Procedure Button */}
      {encounter && !readonly && (
        <Button
          leftSection={<IconPlus size={16} />}
          variant="light"
          size="sm"
          onClick={handleAddProcedure}
        >
          {t('surgeries.add', {
            ns: 'patient-pages',
            defaultValue: 'Add Procedure',
          })}
        </Button>
      )}

      {/* Modal */}
      {encounter && (
        <ProcedureInputModal
          opened={procedureModalOpened}
          onClose={() => setProcedureModalOpened(false)}
          onSave={handleSaveProcedure}
          onSaveSuccess={handleSaveSuccess}
          procedure={editingProcedure}
          mode={modalMode}
          patient={patient}
          encounter={encounter}
          isMedicalHistory={isMedicalHistory}
        />
      )}
    </Stack>
  );
};
